<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content admin-panel">
        <div class="page-header">
          <div class="page-title">
            <h4>{{ tenant?.tenantname || 'Tenant Details' }}</h4>
            <h6>Status: {{ tenant?.status || '-' }} | Plan: {{ tenant?.plan || '-' }}</h6>
          </div>
          <div class="page-actions">
            <button class="btn btn-outline-secondary" type="button" @click="openLimitModal">
              <vue-feather type="settings"></vue-feather>
              Limits
            </button>
            <button class="btn btn-added" type="button" @click="openSystemUserModal">
              <vue-feather type="user-plus"></vue-feather>
              Add Internal User
            </button>
            <router-link class="btn btn-outline-secondary" :to="{ name: 'admin-tenants' }">
              <vue-feather type="arrow-left"></vue-feather>
              Back
            </router-link>
          </div>
        </div>

        <div v-if="error" class="alert alert-danger">{{ error }}</div>
        <div v-if="loading" class="card detail-card">
          <div class="card-body">Loading tenant details...</div>
        </div>

        <template v-if="tenant && !loading">
          <div class="tenant-hero card">
            <div class="card-body">
              <div class="tenant-identity">
                <span class="tenant-avatar">{{ initials(tenant.tenantname) }}</span>
                <div>
                  <h4>{{ tenant.tenantname }}</h4>
                  <p>Created {{ formatDate(tenant.created_at) }}</p>
                </div>
              </div>
              <div class="tenant-meta">
                <span class="plan-pill">{{ tenant.plan }}</span>
                <span class="status-pill" :class="statusClass(tenant.status)">{{ tenant.status }}</span>
              </div>
            </div>
          </div>

          <div class="detail-summary">
            <div v-for="card in summaryCards" :key="card.label" class="summary-card">
              <span :class="card.tone"><vue-feather :type="card.icon"></vue-feather></span>
              <div>
                <strong>{{ card.value }}</strong>
                <small>{{ card.label }}</small>
              </div>
            </div>
          </div>

          <div class="tabs nav-tabs">
            <button v-for="tab in tabs" :key="tab" type="button" class="nav-link" :class="{ active: activeTab === tab }" @click="activeTab = tab">
              <vue-feather :type="tabIcon(tab)"></vue-feather>
              <span>{{ tab }}</span>
            </button>
          </div>

          <div v-if="activeTab === 'Overview'" class="card detail-card">
            <div class="card-body">
              <div class="card-head">
                <div class="title-wrap">
                  <span class="head-icon"><vue-feather type="briefcase"></vue-feather></span>
                  <div>
                    <h4>Overview</h4>
                    <p>Tenant identity and subscription settings.</p>
                  </div>
                </div>
              </div>
              <div class="detail-grid">
                <div><span>Tenant Name</span><strong>{{ tenant.tenantname }}</strong></div>
                <div><span>Created Date</span><strong>{{ formatDate(tenant.created_at) }}</strong></div>
                <div><span>Plan</span><strong>{{ tenant.plan }}</strong></div>
                <div><span>Status</span><strong>{{ tenant.status }}</strong></div>
                <div><span>User Limit</span><strong>{{ tenant.max_users }}</strong></div>
                <div><span>Location Limit</span><strong>{{ tenant.max_locations }}</strong></div>
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'Users'" class="card table-card">
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-hover align-middle">
                  <thead>
                    <tr><th>Name</th><th>Email</th><th>Role</th><th>Locations</th><th>Status</th><th class="text-end">Action</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="user in tenant.users" :key="user.id">
                      <td>{{ user.name || '-' }}</td>
                      <td>{{ user.email }}</td>
                      <td>{{ user.role || '-' }}</td>
                      <td>{{ userLocationLabel(user) }}</td>
                      <td><span :class="user.is_active ? 'badge bg-success' : 'badge bg-secondary'">{{ user.is_active ? 'Active' : 'Inactive' }}</span></td>
                      <td class="text-end">
                        <button class="action-btn" type="button" title="Edit locations" @click="openUserLocationsModal(user)">
                          <vue-feather type="map-pin"></vue-feather>
                        </button>
                      </td>
                    </tr>
                    <tr v-if="!tenant.users.length">
                      <td colspan="6" class="text-center text-muted py-4">No users found.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'Locations'" class="card table-card">
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-hover align-middle">
                  <thead>
                    <tr><th>Name</th><th>Type</th><th>Status</th><th>Default</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="location in tenant.locations" :key="location.locationid">
                      <td>{{ location.locationname }}</td>
                      <td><span class="plan-pill">{{ location.locationtype }}</span></td>
                      <td><span :class="location.is_active ? 'badge bg-success' : 'badge bg-secondary'">{{ location.is_active ? 'Active' : 'Inactive' }}</span></td>
                      <td>{{ location.is_default ? 'YES' : 'NO' }}</td>
                    </tr>
                    <tr v-if="!tenant.locations.length">
                      <td colspan="4" class="text-center text-muted py-4">No locations found.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'Activity'" class="card table-card">
            <div class="card-body">
              <div class="table-responsive">
                <table class="table table-hover align-middle">
                  <thead>
                    <tr><th>Date</th><th>Table</th><th>Action</th><th>Changed By</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="entry in tenant.activity" :key="entry.id">
                      <td>{{ formatDateTime(entry.changed_at) }}</td>
                      <td>{{ entry.table_name }}</td>
                      <td><span class="plan-pill">{{ entry.action }}</span></td>
                      <td>{{ entry.changed_by || '-' }}</td>
                    </tr>
                    <tr v-if="!tenant.activity.length">
                      <td colspan="4" class="text-center text-muted py-4">No activity recorded yet.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>

        <div v-if="showLimitModal" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
          <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">
              <form @submit.prevent="saveLimits">
                <div class="modal-header">
                  <h5 class="modal-title">Edit Tenant Limits</h5>
                  <button class="close" type="button" aria-label="Close" @click="closeLimitModal"><span aria-hidden="true">x</span></button>
                </div>
                <div class="modal-body">
                  <div v-if="modalError" class="alert alert-danger">{{ modalError }}</div>
                  <div class="row g-3">
                    <div class="col-sm-6 col-12">
                      <label class="form-label">Max Users</label>
                      <input v-model.number="limitForm.max_users" class="form-control" min="1" type="number" required />
                    </div>
                    <div class="col-sm-6 col-12">
                      <label class="form-label">Max Locations</label>
                      <input v-model.number="limitForm.max_locations" class="form-control" min="1" type="number" required />
                    </div>
                  </div>
                </div>
                <div class="modal-footer">
                  <button class="btn btn-secondary" type="button" @click="closeLimitModal">Cancel</button>
                  <button class="btn btn-submit" type="submit" :disabled="savingLimits">
                    {{ savingLimits ? 'Saving...' : 'Save Limits' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div v-if="showSystemUserModal" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
          <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">
              <form @submit.prevent="submitSystemUser">
                <div class="modal-header">
                  <h5 class="modal-title">Add System Internal User</h5>
                  <button class="close" type="button" aria-label="Close" @click="closeSystemUserModal"><span aria-hidden="true">x</span></button>
                </div>
                <div class="modal-body">
                  <div v-if="modalError" class="alert alert-danger">{{ modalError }}</div>
                  <div class="row g-3">
                    <div class="col-12">
                      <label class="form-label">Default Location</label>
                      <select v-model="systemUserForm.locationid" class="form-control" required>
                        <option value="">Select location</option>
                        <option v-for="location in activeLocations" :key="location.locationid" :value="location.locationid">
                          {{ location.locationname }}
                        </option>
                      </select>
                    </div>
                    <div class="col-12">
                      <label class="form-label">Name</label>
                      <input v-model.trim="systemUserForm.name" type="text" class="form-control" required />
                    </div>
                    <div class="col-12">
                      <label class="form-label">Email</label>
                      <input v-model.trim="systemUserForm.email" type="email" class="form-control" required />
                    </div>
                    <div class="col-12">
                      <label class="form-label">Password</label>
                      <input v-model="systemUserForm.password" type="password" class="form-control" minlength="6" required />
                    </div>
                    <div class="col-12">
                      <label class="form-label">Confirm Password</label>
                      <input v-model="systemUserForm.confirmPassword" type="password" class="form-control" minlength="6" required />
                    </div>
                    <div class="col-sm-6 col-12">
                      <label class="form-label d-block">Active</label>
                      <input v-model="systemUserForm.is_active" type="checkbox" />
                    </div>
                    <div class="col-sm-6 col-12">
                      <label class="form-label d-block">Force Password Change</label>
                      <input v-model="systemUserForm.must_change_password" type="checkbox" />
                    </div>
                  </div>
                </div>
                <div class="modal-footer">
                  <button class="btn btn-secondary" type="button" @click="closeSystemUserModal">Cancel</button>
                  <button class="btn btn-submit" type="submit" :disabled="savingSystemUser">
                    {{ savingSystemUser ? 'Creating...' : 'Create User' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div v-if="showUserLocationsModal" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
          <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">
              <form @submit.prevent="saveUserLocations">
                <div class="modal-header">
                  <h5 class="modal-title">Edit User Locations</h5>
                  <button class="close" type="button" aria-label="Close" @click="closeUserLocationsModal"><span aria-hidden="true">x</span></button>
                </div>
                <div class="modal-body">
                  <div v-if="modalError" class="alert alert-danger">{{ modalError }}</div>
                  <div class="location-access-grid">
                    <label v-for="location in activeLocations" :key="location.locationid" class="location-access-card">
                      <input v-model="userLocationsForm.locationids" type="checkbox" :value="location.locationid" />
                      <div>
                        <strong>{{ location.locationname }}</strong>
                        <span>{{ location.locationid }}</span>
                      </div>
                    </label>
                  </div>
                  <div class="mt-3">
                    <label class="form-label">Default Location</label>
                    <select v-model="userLocationsForm.locationid" class="form-control" required>
                      <option value="">Select default location</option>
                      <option v-for="location in selectedLocationOptions" :key="location.locationid" :value="location.locationid">
                        {{ location.locationname }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="modal-footer">
                  <button class="btn btn-secondary" type="button" @click="closeUserLocationsModal">Cancel</button>
                  <button class="btn btn-submit" type="submit" :disabled="savingUserLocations">
                    {{ savingUserLocations ? 'Saving...' : 'Save Locations' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div v-if="hasOpenModal" class="modal-backdrop fade show"></div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';

import { createSystemInternalUser, getTenantDetails, updateTenant, updateTenantUserLocations } from '@/services/superAdmin';

const emptySystemUserForm = () => ({
  locationid: '',
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  is_active: true,
  must_change_password: true,
});

export default {
  name: 'SuperAdminTenantDetails',
  data() {
    return {
      tenant: null,
      loading: false,
      error: '',
      tabs: ['Overview', 'Users', 'Locations', 'Activity'],
      activeTab: 'Overview',
      modalError: '',
      showLimitModal: false,
      showSystemUserModal: false,
      showUserLocationsModal: false,
      savingLimits: false,
      savingSystemUser: false,
      savingUserLocations: false,
      selectedUser: null,
      limitForm: {
        max_users: 1,
        max_locations: 1,
      },
      systemUserForm: emptySystemUserForm(),
      userLocationsForm: {
        locationids: [],
        locationid: '',
      },
    };
  },
  computed: {
    summaryCards() {
      return [
        { label: 'Users', value: this.tenant?.users?.length || 0, icon: 'users', tone: 'orange' },
        { label: 'Locations', value: this.tenant?.locations?.length || 0, icon: 'map-pin', tone: 'blue' },
        { label: 'Activity Logs', value: this.tenant?.activity?.length || 0, icon: 'activity', tone: 'green' },
      ];
    },
    activeLocations() {
      return (this.tenant?.locations || []).filter((location) => location.is_active);
    },
    selectedLocationOptions() {
      const selected = new Set((this.userLocationsForm.locationids || []).map(String));
      return this.activeLocations.filter((location) => selected.has(String(location.locationid)));
    },
    hasOpenModal() {
      return this.showLimitModal || this.showSystemUserModal || this.showUserLocationsModal;
    },
  },
  watch: {
    'userLocationsForm.locationids': {
      handler(next) {
        const locationIds = Array.isArray(next) ? next.map(String) : [];
        const deduped = [...new Set(locationIds)];
        if (deduped.length !== locationIds.length) {
          this.userLocationsForm.locationids = deduped;
          return;
        }
        if (!deduped.length) {
          this.userLocationsForm.locationid = '';
          return;
        }
        if (!deduped.includes(this.userLocationsForm.locationid)) {
          this.userLocationsForm.locationid = deduped[0];
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchTenant();
  },
  methods: {
    async fetchTenant() {
      this.loading = true;
      this.error = '';
      try {
        this.tenant = await getTenantDetails(this.$route.params.tenantId);
      } catch (error) {
        this.error = error?.response?.data?.detail || 'Unable to load tenant details';
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString() : '-';
    },
    formatDateTime(value) {
      return value ? new Date(value).toLocaleString() : '-';
    },
    initials(value) {
      return String(value || 'T').trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
    },
    statusClass(status) {
      return String(status || '').toLowerCase();
    },
    tabIcon(tab) {
      return {
        Overview: 'briefcase',
        Users: 'users',
        Locations: 'map-pin',
        Activity: 'activity',
      }[tab] || 'circle';
    },
    userLocationLabel(user) {
      const locations = user.locations || [];
      if (!locations.length) {
        return '-';
      }
      const defaultLocation = locations.find((location) => location.is_default);
      return defaultLocation ? `${defaultLocation.locationname} +${Math.max(0, locations.length - 1)}` : `${locations.length} locations`;
    },
    openLimitModal() {
      this.modalError = '';
      this.limitForm = {
        max_users: this.tenant?.max_users || 1,
        max_locations: this.tenant?.max_locations || 1,
      };
      this.showLimitModal = true;
    },
    closeLimitModal() {
      if (!this.savingLimits) {
        this.showLimitModal = false;
      }
    },
    async saveLimits() {
      this.savingLimits = true;
      this.modalError = '';
      try {
        await updateTenant(this.tenant.tenantid, {
          max_users: this.limitForm.max_users,
          max_locations: this.limitForm.max_locations,
        });
        this.showLimitModal = false;
        await this.fetchTenant();
      } catch (error) {
        this.modalError = error?.response?.data?.detail || 'Unable to save tenant limits.';
      } finally {
        this.savingLimits = false;
      }
    },
    openSystemUserModal() {
      this.modalError = '';
      this.systemUserForm = emptySystemUserForm();
      const defaultLocation = this.activeLocations.find((location) => location.is_default);
      this.systemUserForm.locationid = defaultLocation?.locationid || this.activeLocations[0]?.locationid || '';
      this.showSystemUserModal = true;
    },
    closeSystemUserModal() {
      if (!this.savingSystemUser) {
        this.showSystemUserModal = false;
      }
    },
    async submitSystemUser() {
      if (this.systemUserForm.password !== this.systemUserForm.confirmPassword) {
        Swal.fire({ icon: 'error', title: 'Mismatch', text: 'Password and confirm password must match.' });
        return;
      }

      this.savingSystemUser = true;
      this.modalError = '';
      try {
        await createSystemInternalUser({
          tenant_id: this.tenant.tenantid,
          locationid: this.systemUserForm.locationid,
          name: this.systemUserForm.name,
          email: this.systemUserForm.email,
          password: this.systemUserForm.password,
          is_active: this.systemUserForm.is_active,
          must_change_password: this.systemUserForm.must_change_password,
        });
        this.showSystemUserModal = false;
        this.activeTab = 'Users';
        await this.fetchTenant();
        Swal.fire({ icon: 'success', title: 'System internal user created', timer: 1400, showConfirmButton: false });
      } catch (error) {
        this.modalError = error?.response?.data?.detail || 'Could not create system internal user.';
      } finally {
        this.savingSystemUser = false;
      }
    },
    openUserLocationsModal(user) {
      this.modalError = '';
      this.selectedUser = user;
      const locations = user.locations || [];
      const defaultLocation = locations.find((location) => location.is_default);
      this.userLocationsForm = {
        locationids: locations.map((location) => location.locationid),
        locationid: defaultLocation?.locationid || locations[0]?.locationid || '',
      };
      this.showUserLocationsModal = true;
    },
    closeUserLocationsModal() {
      if (!this.savingUserLocations) {
        this.showUserLocationsModal = false;
      }
    },
    async saveUserLocations() {
      if (!this.userLocationsForm.locationids.length) {
        Swal.fire({ icon: 'error', title: 'Location required', text: 'Select at least one location for this user.' });
        return;
      }

      this.savingUserLocations = true;
      this.modalError = '';
      try {
        await updateTenantUserLocations(this.tenant.tenantid, this.selectedUser.id, {
          locationids: this.userLocationsForm.locationids,
          locationid: this.userLocationsForm.locationid,
        });
        this.showUserLocationsModal = false;
        await this.fetchTenant();
      } catch (error) {
        this.modalError = error?.response?.data?.detail || 'Could not update user locations.';
      } finally {
        this.savingUserLocations = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-panel {
  background: #f7f8fa;
  min-height: calc(100vh - 60px);
}

.page-header .btn,
.page-actions,
.tenant-hero .card-body,
.tenant-identity,
.tenant-meta,
.summary-card,
.tabs button,
.title-wrap {
  align-items: center;
  display: flex;
}

.page-header .btn {
  gap: 8px;
}

.page-actions {
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.page-header .btn :deep(svg),
.summary-card > span :deep(svg),
.head-icon :deep(svg),
.tabs button :deep(svg),
.action-btn :deep(svg) {
  height: 18px;
  stroke-width: 2.4;
  width: 18px;
}

.tenant-hero,
.detail-card,
.table-card {
  border: 1px solid #edf0f3;
  border-radius: 8px;
}

.tenant-hero {
  margin-bottom: 18px;
}

.tenant-hero .card-body {
  gap: 16px;
  justify-content: space-between;
}

.tenant-identity {
  gap: 14px;
}

.tenant-avatar {
  align-items: center;
  background: #fff6ee;
  border-radius: 8px;
  color: #f36f21;
  display: inline-flex;
  flex: 0 0 52px;
  font-size: 16px;
  font-weight: 800;
  height: 52px;
  justify-content: center;
  width: 52px;
}

.tenant-identity h4 {
  color: #111827;
  font-size: 22px;
  font-weight: 800;
  margin: 0;
}

.tenant-identity p {
  color: #637381;
  margin: 4px 0 0;
}

.tenant-meta {
  flex-wrap: wrap;
  gap: 8px;
}

.detail-summary {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 18px;
}

.summary-card {
  background: #fff;
  border: 1px solid #edf0f3;
  border-radius: 8px;
  box-shadow: 0 5px 18px rgba(15, 23, 42, 0.04);
  gap: 14px;
  min-height: 96px;
  padding: 18px;
}

.summary-card > span,
.head-icon {
  align-items: center;
  border-radius: 8px;
  display: inline-flex;
  flex: 0 0 44px;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.summary-card strong,
.summary-card small {
  display: block;
}

.summary-card strong {
  color: #111827;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.1;
}

.summary-card small {
  color: #637381;
  font-size: 13px;
  font-weight: 700;
  margin-top: 5px;
}

.orange { background: #fff7ed; color: #c2410c; }
.green { background: #ecfdf5; color: #15803d; }
.blue { background: #eff6ff; color: #2563eb; }

.tabs {
  border-bottom: 1px solid #dfe3e8;
  display: flex;
  gap: 6px;
  margin-bottom: 18px;
  overflow-x: auto;
}

.tabs button {
  background: transparent;
  border: 0;
  color: #637381;
  gap: 8px;
  font-weight: 700;
  padding: 12px 14px;
  white-space: nowrap;
}

.tabs button.active {
  border-bottom: 2px solid #ff9f43;
  color: #111827;
}

.card-head {
  border-bottom: 1px solid #eef0f4;
  margin-bottom: 16px;
  padding-bottom: 14px;
}

.title-wrap {
  gap: 12px;
}

.head-icon {
  background: #fff6ee;
  color: #f36f21;
  flex-basis: 40px;
  height: 40px;
  width: 40px;
}

.card-head h4 {
  color: #0f172a;
  font-size: 20px;
  font-weight: 800;
  margin: 0;
}

.card-head p {
  color: #64748b;
  font-size: 12px;
  margin: 4px 0 0;
}

.detail-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.detail-grid span {
  color: #637381;
  display: block;
  margin-bottom: 6px;
}

.detail-grid strong {
  color: #111827;
}

.plan-pill,
.status-pill {
  border-radius: 6px;
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  padding: 5px 9px;
}

.plan-pill {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
}

.status-pill.active {
  background: #e9f7f1;
  color: #12805c;
}

.status-pill.suspended {
  background: #fff1e6;
  color: #b54708;
}

.status-pill.trial {
  background: #eaf2ff;
  color: #175cd3;
}

.action-btn {
  align-items: center;
  background: #fff;
  border: 1px solid #e8ebed;
  border-radius: 6px;
  color: #637381;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.modal {
  background: rgba(15, 23, 42, 0.45);
  z-index: 2000;
}

.modal-backdrop {
  z-index: 1990;
}

.modal-content {
  border: 0;
  border-radius: 8px;
}

.modal-header {
  align-items: center;
  border-bottom: 1px solid #eef0f4;
}

.modal-title {
  color: #111827;
  font-weight: 800;
}

.close {
  background: transparent;
  border: 0;
  color: #637381;
  font-size: 22px;
  line-height: 1;
}

.location-access-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
}

.location-access-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  gap: 10px;
  min-height: 72px;
  padding: 12px;
}

.location-access-card input {
  margin-top: 4px;
}

.location-access-card strong,
.location-access-card span {
  display: block;
}

.location-access-card strong {
  color: #111827;
}

.location-access-card span {
  color: #6b7280;
  font-size: 12px;
  margin-top: 4px;
}

@media (max-width: 991px) {
  .detail-summary {
    grid-template-columns: 1fr;
  }

  .tenant-hero .card-body {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
